import React from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, GitMerge, MoreHorizontal, SquareArrowOutUpRight, Trash2 } from 'lucide-react';
import { useEscapeClose } from '../../../hooks/useEscapeClose';

const PANEL_WIDTH = 210;

type LeadRowActionsMenuProps = {
  detailHref: string;
  onOpen: () => void;
  onDelete: () => void;
  /** „In anderes Projekt überführen“ – nur gesetzt, wenn der Nutzer Projekte bearbeiten darf. */
  onMergeInto?: () => void;
  disabled?: boolean;
  /** Sichtbar halten, solange das Menü offen ist — sonst verschwindet der Trigger beim Wegbewegen der Maus. */
  onOpenChange?: (open: boolean) => void;
  className?: string;
};

export function LeadRowActionsMenu({
  detailHref,
  onOpen,
  onDelete,
  onMergeInto,
  disabled = false,
  onOpenChange,
  className = '',
}: LeadRowActionsMenuProps) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);

  const setOpenState = React.useCallback(
    (next: boolean) => {
      setOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  React.useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const left = Math.min(Math.max(8, rect.right - PANEL_WIDTH), window.innerWidth - PANEL_WIDTH - 8);
      const spaceBelow = window.innerHeight - rect.bottom;
      const panelHeight = onMergeInto ? 168 : 132;
      const top = spaceBelow < panelHeight + 28 ? rect.top - 4 - panelHeight : rect.bottom + 4;
      setPos({ top, left });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, onMergeInto]);

  React.useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpenState(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open, setOpenState]);

  useEscapeClose(() => setOpenState(false), open);

  const runAction = (action: () => void) => {
    setOpenState(false);
    action();
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          setOpenState(!open);
        }}
        className={`rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-200/70 hover:text-app-text-muted disabled:opacity-40 ${className}`}
        aria-label="Aktionen"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={panelRef}
              role="menu"
              style={{ top: pos.top, left: pos.left, width: PANEL_WIDTH }}
              className="fixed z-[80] rounded-lg border border-app-border bg-app-surface p-1 shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => runAction(onOpen)}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-app-text-muted hover:bg-app-hover"
              >
                <ExternalLink className="h-4 w-4 text-app-text-subtle" />
                Öffnen
              </button>
              <a
                role="menuitem"
                href={detailHref}
                target="_blank"
                rel="noreferrer"
                onClick={() => setOpenState(false)}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-app-text-muted hover:bg-app-hover"
              >
                <SquareArrowOutUpRight className="h-4 w-4 text-app-text-subtle" />
                In neuem Tab öffnen
              </a>
              {onMergeInto ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => runAction(onMergeInto)}
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-app-text-muted hover:bg-app-hover"
                >
                  <GitMerge className="h-4 w-4 text-app-text-subtle" />
                  In Projekt überführen
                </button>
              ) : null}
              <div className="my-1 h-px bg-app-surface-muted" />
              <button
                type="button"
                role="menuitem"
                onClick={() => runAction(onDelete)}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
                Löschen
              </button>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
